// Raw card inputs: no table-schema registration. Both adventure card renderers share this projection.
import type { AdventureCard, AdventureListResponse, AdventureListRow, AdventurePictureRow, AdventureVideoRow, MyAdventureCard } from './types';
import { compilePictureRow } from '../janus-engine/compile/picture-row-construction';
import { resolveUrl } from './catalog-list';

export type { AdventurePictureRow, AdventureVideoRow, AdventureListRow, AdventureListResponse } from './types';
type Numeric = number | string | null;
export interface AdventureViewer { id?: unknown; primaryRole?: unknown; }

export function adventureViewer(): AdventureViewer {
    return (window as unknown as { FateTide?: { user?: AdventureViewer } }).FateTide?.user ?? {};
}

export function validateAdventureList(data: AdventureListResponse, mode: AdventureListResponse['mode']): void {
    if (data.schema !== 'adventure-list-v1' || data.mode !== mode || !Array.isArray(data.rows)) {
        throw new Error(`Unsupported adventure list schema/mode: ${data.schema}/${data.mode}`);
    }
}

// Match ft_catalog_text's single HTML5 entity decode. DOM text sinks handle the resulting plain text.
function listText(value: string | null): string {
    const text = value ?? '';
    if (!text.includes('&')) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

/** PHP crc32(strtolower(id)) & 0x7fffffff, including UTF-8 byte semantics. */
export function adventureDefaultCoverUrl(id: string): string {
    let crc = 0xffffffff;
    // PHP strtolower() folds ASCII only; String#toLowerCase() would also fold non-ASCII letters.
    for (const byte of new TextEncoder().encode(id.replace(/[A-Z]/g, c => c.toLowerCase()))) {
        crc ^= byte;
        for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    }
    const index = 1 + (((crc ^ 0xffffffff) & 0x7fffffff) % 10);
    return `${BASE_URL_CDN}images/site/adventure-covers/default-cover-${index}-800.webp`;
}

/** Mirror ft_catalog_media_visible: public, employee+, or the exact case-insensitive creator id. */
export function adventureMediaVisible(privacy: Numeric, owner: string | null, viewer: AdventureViewer): boolean {
    if (!Number(privacy)) return true;
    if (['employee', 'admin', 'super_admin'].includes(String(viewer.primaryRole ?? ''))) return true;
    const id = String(viewer.id ?? '');
    return !!owner && !!id && owner.toUpperCase() === id.toUpperCase();
}

function jsonRows<T>(value: string | T[] | null): T[] {
    const rows: unknown = typeof value === 'string' ? JSON.parse(value) : value;
    if (rows === null) return [];
    if (!Array.isArray(rows)) throw new Error('Invalid adventure media rows');
    return rows as T[];
}

// viewer === null shows every header row: My Adventures is the owner's management view, and its old
// endpoint never filtered header media by privacy. Browse cards pass the viewer (ft_catalog_media_visible).
function media(row: AdventureListRow, viewer: AdventureViewer | null): { picture: string; video: string } {
    // JSON_ARRAYAGG has no ordering guarantee. Restore fetch_pictures' explicit SQL order here.
    const pictures = jsonRows(row.adventure_header_pictures).sort((a, b) =>
        Number(b.primary_picture) - Number(a.primary_picture)
        || Number(b.picture_thumbnail) - Number(a.picture_thumbnail)
        || Number(a.picture_number) - Number(b.picture_number)
        || Number(a.picture_i) - Number(b.picture_i));
    const picture = pictures.find(p => !viewer || adventureMediaVisible(p.picture_private, p.picture_created_by_id, viewer));
    // fetch_videos ordered by primary DESC; row order resolves equal-primary clips.
    const videos = jsonRows(row.adventure_header_videos).sort((a, b) =>
        Number(b.video_primary) - Number(a.video_primary) || Number(a.video_i) - Number(b.video_i));
    const video = videos.find(v => !viewer || adventureMediaVisible(v.video_private, v.video_created_by_id, viewer));
    return {
        picture: picture ? String((compilePictureRow(picture, { cdnUrl: BASE_URL_CDN }) as Readonly<Record<string, unknown>>).picture_url ?? '') : '',
        video: video ? `${BASE_URL_CDN}videos/user/adventures/adventure_headers/${video.video_id}.${video.video_file_extension ?? ''}` : '',
    };
}

/** MySQL timestamps have no offset; month/year must not shift with the viewer's timezone. */
export function adventureReleaseDate(value: string | null): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T]|$)/.exec(value ?? '');
    if (!match || Number(match[1]) < 1970 || Number(match[2]) < 1 || Number(match[2]) > 12 || Number(match[3]) < 1 || Number(match[3]) > 31) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[Number(match[2]) - 1]} ${match[1]}`;
}

function common(row: AdventureListRow, viewer: AdventureViewer | null) {
    const name = listText(row.adventure_name);
    const price = Number(row.adventure_price) || 0;
    const header = media(row, viewer);
    return {
        item_id: row.adventure_id, name,
        url: resolveUrl(`adventure.php?adventure_id=${encodeURIComponent(row.adventure_id)}`),
        picture_url: header.picture, video_url: header.video,
        level_begin: String(Math.trunc(Number(row.adventure_level_begin) || 0)),
        level_end: String(Math.trunc(Number(row.adventure_level_end) || 0)),
        is_free: price <= 0, price,
        price_f: price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        release_date: adventureReleaseDate(row.adventure_t),
    };
}

export function adventureBrowseCard(row: AdventureListRow, viewer: AdventureViewer): AdventureCard {
    const card = common(row, viewer);
    const picture_url = card.picture_url || adventureDefaultCoverUrl(row.adventure_id);
    const source = listText(row.adventure_source_name);
    const creator = listText(row.adventure_creator_username);
    const parent_name = row.adventure_parent_id && !Number(row.adventure_parent_deleted) && !Number(row.adventure_parent_private)
        ? listText(row.adventure_parent_name) : '';
    return { ...card, picture_url, poster_url: card.video_url ? picture_url : '',
        price_f: card.is_free ? '' : card.price_f,
        credit_label: source || (Number(row.adventure_creator_al) >= 10 ? creator : ''),
        parent_name, parent_url: parent_name ? resolveUrl(`adventure.php?adventure_id=${encodeURIComponent(row.adventure_parent_id ?? '')}`) : '',
        text: `${card.name} ${parent_name}`.trim().toLowerCase(),
    };
}

export function adventureMyCard(row: AdventureListRow): MyAdventureCard {
    const card = common(row, null);
    const id = encodeURIComponent(row.adventure_id);
    const child_count = Number(row.adventure_child_count) || 0;
    return { ...card, picture_url: card.picture_url || `${BASE_URL_CDN}images/gm_tools/worlds.jpg`,
        locations_url: resolveUrl(`tools/locations/my_locations.php?adventure_id=${id}`),
        encounters_url: resolveUrl(`tools/encounters/my_encounters.php?adventure_id=${id}`),
        edit_url: resolveUrl(`tools/adventures/modify_adventure.php?adventure_id=${id}`),
        media_url: resolveUrl(`tools/media/media_manager.php?type=adventure&id=${id}`),
        is_private: Number(row.adventure_private) !== 0, child_count,
        children_url: child_count > 0 ? resolveUrl(`my_adventures.php?parent_id=${id}`) : '',
        text: card.name.toLowerCase(),
    };
}
